import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";

/**
 * CertificateSVG — Executive Diploma redesign (Phase 13).
 *
 * Landscape 4:3 at viewBox 1200x900. Everything is pure SVG so the same
 * component renders both the on-screen preview AND the exported .svg / PDF.
 *
 * Visual language:
 *   • Parchment off-white background (#FBF8F0) with subtle noise overlay
 *   • Gold outer frame (#D4AF37) with Art-Deco corner ornaments
 *   • Navy accent (#0A1E3F) for inner frame + typography
 *   • Cinzel for title + score headline; Great Vibes for recipient name
 *   • "OFFICIALLY CERTIFIED" medallion+ribbon badge in bottom-center
 *   • QR code bottom-right linking to the public /verify page
 */

const BG = "#FBF8F0";
const GOLD = "#D4AF37";
const GOLD_LIGHT = "#F4D77A";
const GOLD_DEEP = "#8a6d1e";
const NAVY = "#0A1E3F";
const NAVY_MUTED = "#4A4A4A";

function truncate(s, n) {
  if (!s) return "";
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

function formatDate(iso) {
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return "----";
  }
}

/** Public verify URL for the QR code. Must match the deployed public
 *  endpoint shipped in verify_router.py (GET /api/verify?hash=...). */
function buildVerifyUrl(signatureHash) {
  const sig = signatureHash || "unknown";
  // Hard-code the production domain so an exported certificate (printed /
  // PDF / shared SVG) resolves correctly off-site. For QA, the hash echoes
  // back `demo_certificate` or `not_found` which is acceptable.
  return `https://nexalitics.pro/verify?hash=${encodeURIComponent(sig)}`;
}

/** Draw one Art-Deco corner ornament at (ox, oy) with signed scale (sx, sy).
 *  Pure SVG — no CSS. Combines a sunburst + angled fan lines + a gold diamond. */
function CornerOrnament({ ox, oy, sx, sy }) {
  // Points for a 5-ray sunburst centered at (ox, oy) extending outward
  // according to (sx, sy) direction.
  const rays = [];
  for (let i = 0; i < 5; i++) {
    const len = 36 - i * 4;
    const angle = i * 12; // degrees
    const rad = (Math.PI / 180) * angle;
    const x2 = ox + sx * Math.cos(rad) * len;
    const y2 = oy + sy * Math.sin(rad) * len;
    rays.push(
      <line
        key={`ray-${i}`}
        x1={ox}
        y1={oy}
        x2={x2}
        y2={y2}
        stroke={GOLD}
        strokeWidth={0.9}
        opacity={0.85}
      />,
    );
    const rad2 = (Math.PI / 180) * (90 - angle);
    const x3 = ox + sx * Math.cos(rad2) * len;
    const y3 = oy + sy * Math.sin(rad2) * len;
    rays.push(
      <line
        key={`ray2-${i}`}
        x1={ox}
        y1={oy}
        x2={x3}
        y2={y3}
        stroke={GOLD}
        strokeWidth={0.9}
        opacity={0.85}
      />,
    );
  }
  return (
    <g>
      {/* Gold angle bracket */}
      <polyline
        points={`${ox + sx * 48},${oy} ${ox},${oy} ${ox},${oy + sy * 48}`}
        fill="none"
        stroke={GOLD}
        strokeWidth={2.5}
      />
      {/* Navy angle bracket (inner) */}
      <polyline
        points={`${ox + sx * 32},${oy + sy * 10} ${ox + sx * 10},${oy + sy * 10} ${ox + sx * 10},${oy + sy * 32}`}
        fill="none"
        stroke={NAVY}
        strokeWidth={1}
        opacity={0.7}
      />
      {/* Sunburst rays */}
      {rays}
      {/* Gold diamond accent */}
      <polygon
        points={`${ox + sx * 20},${oy + sy * 20} ${ox + sx * 26},${oy + sy * 26} ${ox + sx * 20},${oy + sy * 32} ${ox + sx * 14},${oy + sy * 26}`}
        fill={GOLD}
        opacity={0.9}
      />
    </g>
  );
}

/** OFFICIALLY CERTIFIED medallion + ribbon composite — bottom-center. */
function CertifiedBadge({ label }) {
  // Centered at (600, 740). Medallion r=42 with a horizontal ribbon
  // crossing it.
  return (
    <g>
      {/* Soft shadow under the composite */}
      <ellipse
        cx={600}
        cy={790}
        rx={160}
        ry={10}
        fill="#000"
        opacity={0.08}
      />
      {/* Ribbon band */}
      <polygon
        points="440,748 760,748 760,780 440,780"
        fill={GOLD}
        stroke={GOLD_DEEP}
        strokeWidth={1}
      />
      {/* Ribbon notches (left + right tails) */}
      <polygon
        points="440,748 418,764 440,780"
        fill={GOLD_DEEP}
      />
      <polygon
        points="760,748 782,764 760,780"
        fill={GOLD_DEEP}
      />
      {/* Ribbon label */}
      <text
        x={600}
        y={770}
        textAnchor="middle"
        fontFamily="'Cinzel', 'Playfair Display', Georgia, serif"
        fontSize={16}
        fontWeight={700}
        fill={NAVY}
        style={{ letterSpacing: "0.26em", textTransform: "uppercase" }}
      >
        {label}
      </text>
      {/* Medallion — on top of ribbon */}
      <circle
        cx={600}
        cy={732}
        r={44}
        fill="url(#medallionGrad)"
        stroke={GOLD_DEEP}
        strokeWidth={1.5}
      />
      <circle
        cx={600}
        cy={732}
        r={34}
        fill="none"
        stroke={GOLD_DEEP}
        strokeWidth={0.8}
        opacity={0.75}
      />
      {/* Medallion star monogram */}
      <text
        x={600}
        y={742}
        textAnchor="middle"
        fontFamily="'Cinzel', serif"
        fontSize={28}
        fontWeight={900}
        fill={NAVY}
      >
        ★
      </text>
    </g>
  );
}

/** Decorative divider under the headline — line with central ornament. */
function OrnateDivider({ y = 215 }) {
  return (
    <g>
      <line x1={300} y1={y} x2={560} y2={y} stroke={GOLD} strokeWidth={1} opacity={0.7} />
      <line x1={640} y1={y} x2={900} y2={y} stroke={GOLD} strokeWidth={1} opacity={0.7} />
      <polygon
        points={`590,${y - 6} 600,${y - 12} 610,${y - 6} 610,${y + 6} 600,${y + 12} 590,${y + 6}`}
        fill="none"
        stroke={GOLD}
        strokeWidth={1}
      />
      <circle cx={600} cy={y} r={2.5} fill={GOLD} />
    </g>
  );
}

/** Stylized SVG script for the "N. Studio" signature line. */
function SignatureFlourish({ cx, cy }) {
  return (
    <g>
      <path
        d={`M ${cx - 70} ${cy + 2} C ${cx - 55} ${cy - 20}, ${cx - 30} ${cy + 20}, ${cx} ${cy - 4} S ${cx + 40} ${cy + 15}, ${cx + 70} ${cy - 2}`}
        fill="none"
        stroke={GOLD}
        strokeWidth={1.5}
        strokeLinecap="round"
        opacity={0.9}
      />
      <text
        x={cx}
        y={cy - 2}
        textAnchor="middle"
        fontFamily="'Great Vibes', 'Dancing Script', cursive"
        fontSize={24}
        fill={NAVY}
        opacity={0.85}
      >
        N. Studio
      </text>
    </g>
  );
}

/** Hook that generates a QR data-URL for the verify page. Returns `null`
 *  while loading or on failure; the caller shows a textual fallback. */
function useQrDataUrl(signatureHash) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await QRCode.toDataURL(buildVerifyUrl(signatureHash), {
          errorCorrectionLevel: "M",
          margin: 1,
          width: 180,
          color: { dark: "#0A1E3F", light: "#FFFFFF" },
        });
        if (!cancelled) setUrl(data);
      } catch {
        if (!cancelled) setUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [signatureHash]);
  return url;
}

export const CertificateSVG = forwardRef(function CertificateSVG(
  { userName, promptText, signatureHash, claimedAt, tier = "master", showRewardLine = true, labels = {} },
  ref,
) {
  const prompt = truncate(promptText || "", 120);
  const dateStr = formatDate(claimedAt);
  const sig = signatureHash || "—";
  const qrDataUrl = useQrDataUrl(sig);

  // Default labels — overridable by the parent for i18n. Keeping defaults
  // in English makes the component usable in isolation (e.g. storybook).
  const L = {
    header: labels.header || "Certificate of Prompt Optimization",
    subtitle: labels.subtitle || "This certifies that",
    statement:
      labels.statement ||
      "has achieved Elite Prompt Optimization status with a Nexa-Score of",
    date: labels.date || "Date",
    hash: labels.hash || "Hash",
    by: labels.by || "By",
    signatureAuthorized: labels.signatureAuthorized || "Authorized Signature",
    qrScan: labels.qrScan || "Scan to verify",
    certified: labels.certified || "Officially Certified",
    rewardLine: labels.rewardLine || "Balance Reward: +5 NXT credited",
    scoreTotal: labels.scoreTotal || "/10",
  };

  // Guard: compute display score from prompt-adjacent context — the parent
  // always provides a tier + we show the number if given labels.scoreValue.
  const scoreValue = labels.scoreValue;

  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 1200 900"
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Nexalytics Executive Diploma"
      data-testid="certificate-svg"
      style={{ width: "100%", height: "auto", display: "block" }}
    >
      <defs>
        <radialGradient id="medallionGrad" cx="40%" cy="35%" r="70%">
          <stop offset="0%" stopColor={GOLD_LIGHT} />
          <stop offset="55%" stopColor={GOLD} />
          <stop offset="100%" stopColor={GOLD_DEEP} />
        </radialGradient>
        <linearGradient id="parchmentNoise" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FBF8F0" />
          <stop offset="100%" stopColor="#F5EFDC" />
        </linearGradient>
        {/* Subtle parchment grain via tiled dot pattern */}
        <pattern id="paperGrain" x="0" y="0" width="6" height="6" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.35" fill="#8a6d1e" opacity="0.05" />
          <circle cx="4" cy="3" r="0.3" fill="#8a6d1e" opacity="0.04" />
        </pattern>
      </defs>

      {/* Background */}
      <rect width="1200" height="900" fill="url(#parchmentNoise)" />
      <rect width="1200" height="900" fill="url(#paperGrain)" />

      {/* Outer gold frame */}
      <rect x="30" y="30" width="1140" height="840" fill="none" stroke={GOLD} strokeWidth="3" />
      {/* Inner navy frame */}
      <rect x="45" y="45" width="1110" height="810" fill="none" stroke={NAVY} strokeWidth="1" />

      {/* Four Art-Deco corner ornaments */}
      <CornerOrnament ox={65} oy={65} sx={1} sy={1} />
      <CornerOrnament ox={1135} oy={65} sx={-1} sy={1} />
      <CornerOrnament ox={65} oy={835} sx={1} sy={-1} />
      <CornerOrnament ox={1135} oy={835} sx={-1} sy={-1} />

      {/* Headline */}
      <text
        x="600"
        y="155"
        textAnchor="middle"
        fontFamily="'Cinzel', 'Playfair Display', Georgia, serif"
        fontSize="36"
        fontWeight="700"
        fill={NAVY}
        style={{ letterSpacing: "0.14em", textTransform: "uppercase" }}
      >
        {L.header}
      </text>

      <OrnateDivider y={190} />

      {/* Subtitle */}
      <text
        x="600"
        y="232"
        textAnchor="middle"
        fontFamily="Inter, system-ui, sans-serif"
        fontSize="18"
        fill={NAVY_MUTED}
      >
        {L.subtitle}
      </text>

      {/* Recipient name — Great Vibes cursive */}
      <text
        x="600"
        y="325"
        textAnchor="middle"
        fontFamily="'Great Vibes', 'Dancing Script', cursive"
        fontSize="76"
        fill={NAVY}
        style={{ filter: "drop-shadow(0 2px 6px rgba(212,175,55,0.25))" }}
      >
        {userName || "—"}
      </text>

      {/* Statement */}
      <text
        x="600"
        y="372"
        textAnchor="middle"
        fontFamily="Inter, system-ui, sans-serif"
        fontSize="16"
        fill={NAVY_MUTED}
      >
        {L.statement}
      </text>

      {/* Score headline */}
      {typeof scoreValue === "number" || typeof scoreValue === "string" ? (
        <text
          x="600"
          y="440"
          textAnchor="middle"
          fontFamily="'Cinzel', 'Playfair Display', Georgia, serif"
          fontSize="60"
          fontWeight="700"
          fill={NAVY}
        >
          {scoreValue}
          <tspan fontSize="28" fill={GOLD_DEEP}>
            {" "}
            {L.scoreTotal}
          </tspan>
        </text>
      ) : null}

      {/* Optional prompt excerpt */}
      {prompt ? (
        <text
          x="600"
          y="500"
          textAnchor="middle"
          fontFamily="'Cinzel', 'Playfair Display', Georgia, serif"
          fontStyle="italic"
          fontSize="14"
          fill="#5A5A5A"
        >
          "{prompt}"
        </text>
      ) : null}

      {/* Signature line + authorized caption */}
      <SignatureFlourish cx={300} cy={660} />
      <line
        x1={210}
        y1={678}
        x2={390}
        y2={678}
        stroke={GOLD}
        strokeWidth={1}
        opacity={0.75}
      />
      <text
        x={300}
        y={700}
        textAnchor="middle"
        fontFamily="Inter, system-ui, sans-serif"
        fontSize="11"
        fill={NAVY_MUTED}
        style={{ letterSpacing: "0.18em", textTransform: "uppercase" }}
      >
        {L.signatureAuthorized}
      </text>

      {/* OFFICIALLY CERTIFIED badge */}
      <CertifiedBadge label={L.certified} />

      {/* QR code — bottom-right. Fallback: show the sig in text if the data
          URL did not resolve. */}
      <g>
        {qrDataUrl ? (
          <image
            href={qrDataUrl}
            x={990}
            y={620}
            width={110}
            height={110}
            preserveAspectRatio="xMidYMid meet"
          />
        ) : (
          <>
            <rect
              x={990}
              y={620}
              width={110}
              height={110}
              fill="#FFFFFF"
              stroke={NAVY}
              strokeWidth={1}
            />
            <text
              x={1045}
              y={670}
              textAnchor="middle"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              fontSize={10}
              fill={NAVY}
            >
              VERIFY
            </text>
            <text
              x={1045}
              y={690}
              textAnchor="middle"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              fontSize={9}
              fill={NAVY}
            >
              {sig.slice(0, 12)}
            </text>
          </>
        )}
        <text
          x={1045}
          y={750}
          textAnchor="middle"
          fontFamily="'Cinzel', 'Playfair Display', Georgia, serif"
          fontSize={10}
          fill={NAVY}
          style={{ letterSpacing: "0.22em", textTransform: "uppercase" }}
        >
          {L.qrScan}
        </text>
      </g>

      {/* Metadata footer — 3-column row of DATE / HASH / BY */}
      <g fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace" fontSize="11">
        <text x="80" y="830" fill={NAVY}>
          {L.date}: {dateStr}
        </text>
        <text x="600" y="830" textAnchor="middle" fill={NAVY}>
          {L.hash}: {sig}
        </text>
        <text x="1120" y="830" textAnchor="end" fill={NAVY}>
          {L.by}: NexaStudio
        </text>
      </g>

      {/* Optional reward line (kept for backwards compat with Phase 5/7) */}
      {showRewardLine ? (
        <text
          x="600"
          y="852"
          textAnchor="middle"
          fontFamily="Inter, system-ui, sans-serif"
          fontSize="11"
          fill={GOLD_DEEP}
          opacity="0.85"
        >
          {L.rewardLine}
        </text>
      ) : null}
    </svg>
  );
});

// --------------------------------------------------------------------------
// Export helpers
// --------------------------------------------------------------------------

/** Serialize an <svg/> DOM node to a downloadable Blob and trigger download.
 *  Retained from Phase 5 for backwards compat / fallbacks. */
export function exportCertificateSVG(svgEl, filename, opts = {}) {
  if (!svgEl) return;
  const { demoMode = false, signature = "" } = opts;
  const serializer = new XMLSerializer();
  let raw = serializer.serializeToString(svgEl);

  if (demoMode) {
    const metadataBlock =
      `<metadata>` +
      `<rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#" ` +
      `xmlns:dc="http://purl.org/dc/elements/1.1/">` +
      `<rdf:Description>` +
      `<dc:type>demo_certificate</dc:type>` +
      `<dc:rights>NOT VERIFIED — Demonstration only</dc:rights>` +
      `<dc:identifier>${signature || "DEMO"}</dc:identifier>` +
      `</rdf:Description>` +
      `</rdf:RDF>` +
      `</metadata>`;
    raw = raw.replace(/(<svg\b[^>]*>)/, `$1${metadataBlock}`);
  }

  const parts = [];
  parts.push(`<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n`);
  if (demoMode) {
    parts.push(`<!-- NexaLytics DEMO Certificate — Not a verified credential -->\n`);
  }
  parts.push(raw);
  const withDecl = parts.join("");

  const blob = new Blob([withDecl], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "nexalytics-certificate.svg";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

/** Render the current <svg/> DOM node into a landscape A4 PDF via jsPDF +
 *  svg2pdf.js. Returns a Promise resolving on success or rejecting with
 *  the thrown error so the caller can fall back to print. */
export async function exportCertificatePDF(svgEl, filename) {
  if (!svgEl) throw new Error("svg element missing");
  // Dynamic imports to keep the first-load bundle lean. ~250KB combined
  // but only loaded when the user actually asks for a PDF.
  const [{ jsPDF }, { svg2pdf }] = await Promise.all([
    import("jspdf"),
    import("svg2pdf.js"),
  ]);
  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4", // 842 x 595 pt
  });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  // Target width = pageW with a 24pt margin on each side. The SVG viewBox
  // is 1200x900 (4:3) so scale uniformly.
  const margin = 24;
  const targetW = pageW - margin * 2;
  const targetH = (targetW * 900) / 1200;
  const offsetY = (pageH - targetH) / 2;

  await svg2pdf(svgEl, pdf, {
    x: margin,
    y: offsetY,
    width: targetW,
    height: targetH,
  });
  pdf.save(filename || "nexalytics-certificate.pdf");
}

export default CertificateSVG;
